// src/ui/screens/post/AddWantScreen.jsx

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  doc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage';
import { db, auth, storage } from '../../../firebase';
import notificationService from '../../../services/notificationService';

const kOrange = '#FF8C08';
const kBg = '#08090B';
const kCard = '#23262C';
const kCardLight = '#2C2F36';
const kDivider = 'rgba(255,255,255,0.12)';

const kMaxImages = 4;

// Viewport width at which the desktop layout kicks in.
const DESKTOP_BREAKPOINT = 900;

/** Expiry options for a Want post. */
export const WantExpiryOption = Object.freeze({
  oneDay: 'oneDay',
  threeDays: 'threeDays',
  fiveDays: 'fiveDays',
  oneWeek: 'oneWeek',
});

const EXPIRY_META = {
  [WantExpiryOption.oneDay]: { label: '1 Day', days: 1 },
  [WantExpiryOption.threeDays]: { label: '3 Days', days: 3 },
  [WantExpiryOption.fiveDays]: { label: '5 Days', days: 5 },
  [WantExpiryOption.oneWeek]: { label: '1 Week', days: 7 },
};

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * Tracks whether the viewport is at desktop width.
 */
function useIsDesktop(breakpoint = DESKTOP_BREAKPOINT) {
  const query = `(min-width: ${breakpoint}px)`;
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return isDesktop;
}

/**
 * Mirrors Flutter's pickMultiImage(maxWidth, maxHeight, imageQuality):
 * downscale to fit inside the box and re-encode as JPEG. Falls back to the
 * original file if anything goes wrong.
 */
async function compressImage(file, maxW, maxH, quality) {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxW / bitmap.width, maxH / bitmap.height);
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality)
    );
    return blob || file;
  } catch (_) {
    return file;
  }
}

// ── Draft that survives the picker round trip ────────────────────────────
//
// In Flutter, `await Navigator.push(CategoryPickerPage)` keeps this screen
// mounted while the picker is open. On web the pickers are separate routes,
// so returning re-mounts this screen and would wipe everything typed so
// far. This module-level draft is saved on every change, restored only when
// coming back from a picker, and cleared on post / normal exit.

let wantDraft = null;
let keepDraft = false;

const takeDraft = () => (keepDraft && wantDraft ? wantDraft : null);

/**
 * Add Want screen. Mirrors the Flutter AddWantScreen:
 *  - Category + subcategory pickers
 *  - Details text area
 *  - Budget (with flexible toggle)
 *  - Location (district + division pickers)
 *  - Optional up to 4 look-alike photos
 *  - Expiry period chips
 *  - Posts to Firestore `wants/{id}` with progress overlay
 *
 * Picker pages must navigate back to this route with:
 *   navigate('/post/add-want', {
 *     replace: true,
 *     state: { picked: <category|district|division object>, field: 'category' | 'district' | 'division' },
 *   })
 */
export default function AddWantScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();

  const initial = useRef(takeDraft()).current;

  const [details, setDetails] = useState(initial?.details ?? '');
  const [budget, setBudget] = useState(initial?.budget ?? '');
  const [isBudgetFlexible, setIsBudgetFlexible] = useState(
    initial?.isBudgetFlexible ?? false
  );

  const [selectedCategory, setSelectedCategory] = useState(
    initial?.selectedCategory ?? null
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState(
    initial?.selectedSubcategory ?? null
  );
  const [selectedDistrict, setSelectedDistrict] = useState(
    initial?.selectedDistrict ?? null
  );
  const [selectedDivision, setSelectedDivision] = useState(
    initial?.selectedDivision ?? null
  );

  const [selectedExpiry, setSelectedExpiry] = useState(
    initial?.selectedExpiry ?? WantExpiryOption.threeDays
  );

  const [images, setImages] = useState(initial?.images ?? []); // [{ file, previewUrl }]

  const [fieldErrors, setFieldErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState('');

  const [snack, setSnack] = useState(null);
  const fileInputRef = useRef(null);
  const snackTimer = useRef(null);

  // ── Draft bookkeeping ────────────────────────────────────────────────

  useEffect(() => {
    // We're mounted now — a later unmount is a real exit unless a picker
    // navigation sets keepDraft again first.
    keepDraft = false;
    return () => {
      if (!keepDraft) wantDraft = null;
      if (snackTimer.current) clearTimeout(snackTimer.current);
    };
  }, []);

  useEffect(() => {
    wantDraft = {
      details,
      budget,
      isBudgetFlexible,
      selectedCategory,
      selectedSubcategory,
      selectedDistrict,
      selectedDivision,
      selectedExpiry,
      images,
    };
  }, [
    details,
    budget,
    isBudgetFlexible,
    selectedCategory,
    selectedSubcategory,
    selectedDistrict,
    selectedDivision,
    selectedExpiry,
    images,
  ]);

  // ── Consume picked value handed back by a picker page ────────────────

  useEffect(() => {
    const picked = location.state?.picked;
    const field = location.state?.field;
    if (!picked || !field) return;

    if (field === 'category') {
      setSelectedCategory(picked);
      setSelectedSubcategory(null);
      setFieldErrors((e) => ({ ...e, category: undefined }));
    } else if (field === 'district') {
      setSelectedDistrict(picked);
      setSelectedDivision(null);
      setFieldErrors((e) => ({ ...e, district: undefined }));
    } else if (field === 'division') {
      setSelectedDivision(picked);
      setFieldErrors((e) => ({ ...e, division: undefined }));
    }

    // Clear the state so a refresh doesn't re-apply the same pick.
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // ── Helpers ──────────────────────────────────────────────────────────

  const showSnack = (msg, isError = false) => {
    setSnack({ msg, isError });
    if (snackTimer.current) clearTimeout(snackTimer.current);
    snackTimer.current = setTimeout(() => setSnack(null), 3000);
  };

  // Same as Flutter's Navigator.pop — with a fallback for direct links.
  const leave = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  // ── Pickers ──────────────────────────────────────────────────────────

  const goToPicker = (path, state) => {
    keepDraft = true; // keep the form while the picker route is open
    navigate(path, { state });
  };

  const pickCategory = () => {
    goToPicker('/post/category', {
      returnTo: '/post/add-want',
      field: 'category',
    });
  };

  const pickDistrict = () => {
    goToPicker('/post/district', {
      returnTo: '/post/add-want',
      field: 'district',
    });
  };

  const pickDivision = () => {
    if (!selectedDistrict) {
      showSnack('Please select a district first', true);
      return;
    }
    goToPicker('/post/division', {
      returnTo: '/post/add-want',
      field: 'division',
      districtName: selectedDistrict.name,
    });
  };

  // ── Images ───────────────────────────────────────────────────────────

  const pickImages = (files) => {
    if (!files || files.length === 0) return;
    if (images.length >= kMaxImages) {
      showSnack(`Maximum ${kMaxImages} photos allowed`, true);
      return;
    }
    const remaining = kMaxImages - images.length;
    const next = Array.from(files)
      .slice(0, remaining)
      .map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }));
    setImages((prev) => [...prev, ...next]);
  };

  const removeImage = (index) => {
    setImages((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].previewUrl);
      next.splice(index, 1);
      return next;
    });
  };

  // ── Validation ───────────────────────────────────────────────────────

  const validate = () => {
    const errors = {};
    if (!selectedCategory) errors.category = 'Please select a category';
    if (selectedCategory && !selectedSubcategory)
      errors.subcategory = 'Please select a sub-category';
    if (!details.trim())
      errors.details = "Describe what you're looking for";
    if (!isBudgetFlexible && !budget.trim())
      errors.budget = 'Enter a budget or mark as flexible';
    if (!selectedDistrict) errors.district = 'Please select a district';
    if (!selectedDivision) errors.division = 'Please select a division';

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Submit ───────────────────────────────────────────────────────────
  //
  // Wants don't require the seller-verification flow that listings do, so
  // this posts straight to Firestore once validation passes.

  const submit = async () => {
    if (!validate()) {
      showSnack('Please fill all required fields', true);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatusText('Preparing your want…');

    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Not signed in');

      const wantId = newId();
      const imageUrls = [];

      const totalSteps = images.length === 0 ? 1 : images.length;
      let completedSteps = 0;

      // ── Upload photos (optional) ─────────────────────────────────────
      for (let i = 0; i < images.length; i++) {
        setUploadStatusText(`Uploading photo ${i + 1} of ${images.length}…`);

        // Flutter: maxWidth 1920, maxHeight 1080, imageQuality 85
        const blob = await compressImage(images[i].file, 1920, 1080, 0.85);

        const fileRef = storageRef(
          storage,
          `wants/${uid}/${wantId}/image_${i}.jpg`
        );
        const task = uploadBytesResumable(fileRef, blob, {
          contentType: 'image/jpeg',
        });

        await new Promise((resolve, reject) => {
          task.on(
            'state_changed',
            (snap) => {
              const imageProgress = snap.bytesTransferred / snap.totalBytes;
              const overall =
                ((completedSteps + imageProgress) / totalSteps) * 0.85;
              setUploadProgress(overall);
            },
            reject,
            resolve
          );
        });

        imageUrls.push(await getDownloadURL(fileRef));
        completedSteps++;
      }

      setUploadProgress(0.9);
      setUploadStatusText('Saving your want…');

      const days = EXPIRY_META[selectedExpiry].days;
      const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
      const trimmedDetails = details.trim();

      await setDoc(doc(db, 'wants', wantId), {
        id: wantId,
        uid,
        category: selectedCategory.title,
        subcategory: selectedSubcategory,
        details: trimmedDetails,
        currency: 'UGX',
        budget: isBudgetFlexible
          ? null
          : Number.parseFloat(budget.trim()) || null,
        isBudgetFlexible,
        country: 'Uganda',
        district: selectedDistrict.name,
        division: selectedDivision.name,
        imageUrls,
        expiryDays: days,
        status: 'active',
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAt),
      });

      // Fire-and-forget — onWantPosted resolves its own recipient set.
      try {
        notificationService.onWantPosted({
          wantId,
          details: trimmedDetails,
          thumbnailUrl: imageUrls.length > 0 ? imageUrls[0] : null,
        });
      } catch (e) {
        console.warn('Want notify error:', e);
      }

      setUploadProgress(1);
      setUploadStatusText('Posted successfully!');

      await new Promise((r) => setTimeout(r, 800));

      // Posted — the draft is no longer needed.
      images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      wantDraft = null;
      keepDraft = false;

      leave();
    } catch (e) {
      console.error('Want upload error:', e);
      setIsUploading(false);
      showSnack('Could not post your want. Try again.', true);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: kBg,
        position: 'relative',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: isDesktop ? 1040 : 'none',
          margin: '0 auto',
        }}
      >
        <div
          style={{
            padding: isDesktop ? '32px 24px 4px' : '8px 16px 4px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <BackButton onClick={leave} />
          <h1
            style={{
              color: '#fff',
              fontSize: isDesktop ? 24 : 20,
              fontWeight: 700,
              margin: 0,
            }}
          >
            Post a Want
          </h1>
        </div>

        <div style={{ padding: isDesktop ? '16px 24px 40px' : '8px 16px 32px' }}>
          <div
            style={
              isDesktop
                ? {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 24,
                    alignItems: 'start',
                  }
                : undefined
            }
          >
            {/* Left column (desktop) */}
            <div>
              {/* Category */}
              <Section icon={<CategoryIcon />} title="Category">
                <PickerField
                  label="Category"
                  value={selectedCategory?.title}
                  error={fieldErrors.category}
                  onClick={pickCategory}
                  icon={
                    selectedCategory?.svgPath ? (
                      <img
                        src={selectedCategory.svgPath}
                        alt=""
                        style={{
                          width: 18,
                          height: 18,
                          filter:
                            'invert(58%) sepia(82%) saturate(2000%) hue-rotate(0deg) brightness(100%) contrast(101%)',
                        }}
                      />
                    ) : null
                  }
                />
                {selectedCategory && (
                  <div style={{ marginTop: 12 }}>
                    <SubcategoryField
                      subs={selectedCategory.subCategories ?? []}
                      selected={selectedSubcategory}
                      error={fieldErrors.subcategory}
                      onSelect={(sub) => {
                        setSelectedSubcategory(sub);
                        setFieldErrors((e) => ({
                          ...e,
                          subcategory: undefined,
                        }));
                      }}
                    />
                  </div>
                )}
              </Section>

              {/* Details */}
              <Section icon={<DescriptionIcon />} title="Want Details">
                <TextField
                  value={details}
                  onChange={(v) => {
                    setDetails(v);
                    setFieldErrors((e) => ({ ...e, details: undefined }));
                  }}
                  label="What are you looking for?"
                  hint="e.g. 2-bedroom apartment in Ntinda, furnished, near the main road…"
                  maxLines={5}
                  error={fieldErrors.details}
                />
              </Section>

              {/* Budget */}
              <Section icon={<PaymentsIcon />} title="Budget">
                <div
                  style={{
                    padding: '10px 14px',
                    background: 'rgba(255,140,8,0.10)',
                    border: '1px solid rgba(255,140,8,0.30)',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    color: kOrange,
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  <CurrencyIcon />
                  Currency: Ugandan Shillings (UGX)
                </div>

                <div style={{ height: 12 }} />

                {!isBudgetFlexible && (
                  <TextField
                    value={budget}
                    onChange={(v) => {
                      setBudget(v.replace(/[^0-9]/g, ''));
                      setFieldErrors((e) => ({ ...e, budget: undefined }));
                    }}
                    label="Budget (UGX)"
                    hint="e.g. 150000000"
                    inputMode="numeric"
                    error={fieldErrors.budget}
                  />
                )}

                <div style={{ height: 8 }} />

                <div
                  onClick={() => {
                    const next = !isBudgetFlexible;
                    setIsBudgetFlexible(next);
                    if (next) setBudget('');
                    setFieldErrors((e) => ({ ...e, budget: undefined }));
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: isBudgetFlexible ? kOrange : 'transparent',
                      border: `1.5px solid ${
                        isBudgetFlexible ? kOrange : 'rgba(255,255,255,0.38)'
                      }`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontSize: 14,
                      fontWeight: 800,
                      transition: 'all 0.18s',
                    }}
                  >
                    {isBudgetFlexible && '✓'}
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
                    Budget is Flexible
                  </span>
                </div>
              </Section>
            </div>

            {/* Right column (desktop) */}
            <div>
              {/* Location */}
              <Section icon={<LocationIcon />} title="Location">
                <div
                  style={{
                    padding: '14px',
                    background: kCardLight,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <span style={{ fontSize: 20 }}>🇺🇬</span>
                  <span
                    style={{
                      color: '#fff',
                      fontSize: 15,
                      fontWeight: 500,
                      flex: 1,
                    }}
                  >
                    Uganda
                  </span>
                  <LockIcon />
                </div>

                <div style={{ height: 12 }} />

                <PickerField
                  label="District"
                  value={selectedDistrict?.name}
                  subtitle={
                    selectedDistrict ? `${selectedDistrict.region} Region` : null
                  }
                  error={fieldErrors.district}
                  onClick={pickDistrict}
                />

                <div style={{ height: 12 }} />

                <PickerField
                  label="Division / Sub-county"
                  value={selectedDivision?.name}
                  subtitle={
                    selectedDistrict && !selectedDivision
                      ? `Tap to select division in ${selectedDistrict.name}`
                      : null
                  }
                  error={fieldErrors.division}
                  onClick={pickDivision}
                />
              </Section>

              {/* Look-alike photos */}
              <Section
                icon={<PhotoLibraryIcon />}
                title="Look-alike(Optional)"
                subtitle={`${images.length}/${kMaxImages} photos`}
              >
                <ImageGrid
                  images={images}
                  max={kMaxImages}
                  onAdd={() => fileInputRef.current?.click()}
                  onRemove={removeImage}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    pickImages(e.target.files);
                    e.target.value = '';
                  }}
                />
              </Section>

              {/* Expiry */}
              <Section icon={<TimerIcon />} title="Expiry Period">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.values(WantExpiryOption).map((opt) => {
                    const isSelected = selectedExpiry === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => setSelectedExpiry(opt)}
                        style={{
                          padding: '9px 16px',
                          borderRadius: 20,
                          background: isSelected ? kOrange : kCardLight,
                          border: `1px solid ${
                            isSelected ? kOrange : 'rgba(255,255,255,0.12)'
                          }`,
                          color: isSelected ? '#000' : 'rgba(255,255,255,0.7)',
                          fontSize: 13,
                          fontWeight: isSelected ? 600 : 400,
                          cursor: 'pointer',
                          transition: 'all 0.18s',
                        }}
                      >
                        {EXPIRY_META[opt].label}
                      </button>
                    );
                  })}
                </div>
              </Section>
            </div>
          </div>

          <div style={{ height: 24 }} />

          <div
            style={{
              width: '100%',
              maxWidth: isDesktop ? 480 : 'none',
              margin: '0 auto',
            }}
          >
            <button
              onClick={submit}
              disabled={isUploading}
              style={{
                width: '100%',
                height: 54,
                background: isUploading ? 'rgba(255,140,8,0.4)' : kOrange,
                color: '#000',
                fontSize: 16,
                fontWeight: 700,
                border: 'none',
                borderRadius: 16,
                cursor: isUploading ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              <SearchIcon />
              Post Want
            </button>
          </div>

          <div style={{ height: 16 }} />
        </div>
      </div>

      {/* Upload overlay */}
      {isUploading && (
        <UploadOverlay
          progress={uploadProgress}
          statusText={uploadStatusText}
        />
      )}

      {/* Snackbar */}
      {snack && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: snack.isError ? '#ff5252' : kOrange,
            color: '#fff',
            padding: '12px 20px',
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            zIndex: 1000,
            maxWidth: '90%',
          }}
        >
          {snack.msg}
        </div>
      )}
    </div>
  );
}

// ── Section wrapper ──────────────────────────────────────────────────────

function Section({ icon, title, subtitle, children }) {
  return (
    <div
      style={{
        background: kCard,
        borderRadius: 16,
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: 'rgba(255,140,8,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: kOrange,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              marginLeft: 'auto',
              color: 'rgba(255,255,255,0.38)',
              fontSize: 12,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      <div
        style={{
          height: 1,
          background: kDivider,
          margin: '14px 0',
        }}
      />
      {children}
    </div>
  );
}

// ── Picker field ─────────────────────────────────────────────────────────

function PickerField({ label, value, subtitle, error, icon, onClick }) {
  const hasValue = value != null && value.length > 0;
  return (
    <div>
      <div
        onClick={onClick}
        style={{
          padding: '14px',
          background: kCardLight,
          borderRadius: 12,
          border: error ? '1px solid #ff5252' : 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
        }}
      >
        {icon}
        <div style={{ flex: 1 }}>
          <div
            style={{
              color: hasValue ? '#fff' : 'rgba(255,255,255,0.38)',
              fontSize: 15,
              fontWeight: hasValue ? 500 : 400,
            }}
          >
            {hasValue ? value : label}
          </div>
          {subtitle && (
            <div
              style={{
                color: 'rgba(255,255,255,0.38)',
                fontSize: 12,
                marginTop: 2,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        <span style={{ color: kOrange, fontSize: 20 }}>›</span>
      </div>
      {error && (
        <div
          style={{
            color: '#ff5252',
            fontSize: 11,
            marginTop: 4,
            marginLeft: 4,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

// ── Subcategory chips ────────────────────────────────────────────────────

function SubcategoryField({ subs, selected, error, onSelect }) {
  return (
    <div>
      <div
        style={{
          color: 'rgba(255,255,255,0.54)',
          fontSize: 13,
          fontWeight: 500,
          marginBottom: 8,
        }}
      >
        Sub-category
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {subs.map((sub) => {
          const isSelected = selected === sub;
          return (
            <button
              key={sub}
              onClick={() => onSelect(sub)}
              style={{
                padding: '8px 14px',
                borderRadius: 20,
                background: isSelected ? kOrange : kCardLight,
                border: `1px solid ${
                  isSelected ? kOrange : 'rgba(255,255,255,0.12)'
                }`,
                color: isSelected ? '#000' : 'rgba(255,255,255,0.7)',
                fontSize: 13,
                fontWeight: isSelected ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.18s',
              }}
            >
              {sub}
            </button>
          );
        })}
      </div>
      {error && (
        <div
          style={{
            color: '#ff5252',
            fontSize: 11,
            marginTop: 6,
            marginLeft: 4,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

// ── Text field ───────────────────────────────────────────────────────────

function TextField({
  value,
  onChange,
  label,
  hint,
  maxLines = 1,
  inputMode = 'text',
  error,
}) {
  return (
    <div>
      <div
        style={{
          background: kCardLight,
          borderRadius: 12,
          border: error ? '1px solid #ff5252' : '1px solid transparent',
          padding: '10px 14px',
          transition: 'border-color 0.15s',
        }}
      >
        <label
          style={{
            display: 'block',
            color: 'rgba(255,255,255,0.38)',
            fontSize: 12,
            marginBottom: 4,
          }}
        >
          {label}
        </label>
        {maxLines > 1 ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={hint}
            rows={maxLines}
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'vertical',
              color: '#fff',
              fontSize: 15,
              caretColor: kOrange,
              padding: 0,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={hint}
            inputMode={inputMode}
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#fff',
              fontSize: 15,
              caretColor: kOrange,
              padding: 0,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        )}
      </div>
      {error && (
        <div
          style={{
            color: '#ff5252',
            fontSize: 11,
            marginTop: 4,
            marginLeft: 4,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

// ── Image grid ───────────────────────────────────────────────────────────

function ImageGrid({ images, max, onAdd, onRemove }) {
  const showAddTile = images.length < max;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
      }}
    >
      {images.map((img, i) => (
        <div
          key={img.previewUrl}
          style={{
            position: 'relative',
            aspectRatio: '1',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          <img
            src={img.previewUrl}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <button
            onClick={() => onRemove(i)}
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.87)',
              border: 'none',
              color: '#fff',
              fontSize: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            ✕
          </button>
        </div>
      ))}

      {showAddTile && (
        <button
          onClick={onAdd}
          style={{
            aspectRatio: '1',
            background: kCardLight,
            border: '1px solid rgba(255,140,8,0.40)',
            borderRadius: 12,
            color: kOrange,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
          }}
        >
          <AddPhotoIcon />
          <span style={{ fontSize: 11, fontWeight: 500 }}>
            {images.length === 0 ? 'Add Photos' : 'Add More'}
          </span>
        </button>
      )}
    </div>
  );
}

// ── Upload overlay ───────────────────────────────────────────────────────

function UploadOverlay({ progress, statusText }) {
  const percent = Math.floor(progress * 100);
  const isComplete = progress >= 1;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 40,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          background: kCard,
          border: '1px solid rgba(255,140,8,0.20)',
          borderRadius: 24,
          padding: 28,
          width: '100%',
          maxWidth: 360,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: isComplete
              ? 'rgba(255,140,8,0.15)'
              : 'rgba(255,140,8,0.10)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: kOrange,
            transition: 'all 0.3s',
          }}
        >
          {isComplete ? <CheckIcon size={36} /> : <SearchIcon size={32} />}
        </div>

        <div
          style={{
            marginTop: 20,
            color: isComplete ? kOrange : '#fff',
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: -1,
          }}
        >
          {isComplete ? 'Posted!' : `${percent}%`}
        </div>

        <div
          style={{
            marginTop: 6,
            color: 'rgba(255,255,255,0.54)',
            fontSize: 13,
            textAlign: 'center',
          }}
        >
          {statusText}
        </div>

        <div
          style={{
            marginTop: 20,
            width: '100%',
            height: 6,
            background: 'rgba(255,255,255,0.12)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${percent}%`,
              height: '100%',
              background: kOrange,
              transition: 'width 0.2s',
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Small icons ──────────────────────────────────────────────────────────

function BackButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 40,
        height: 40,
        borderRadius: 20,
        background: kCard,
        border: `1px solid ${kDivider}`,
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      ←
    </button>
  );
}

function CategoryIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function DescriptionIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
      <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function PaymentsIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function CurrencyIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
      <path d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function PhotoLibraryIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="9" cy="10" r="1.8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3 17l5-5 5 5 3-3 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function TimerIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 9v4l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M9 2h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function AddPhotoIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path d="M4 16l4-4 4 4 4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="9" cy="9" r="1.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="10" width="14" height="10" rx="2" stroke="rgba(255,255,255,0.38)" strokeWidth="1.8" />
      <path d="M8 10V7a4 4 0 018 0v3" stroke="rgba(255,255,255,0.38)" strokeWidth="1.8" />
    </svg>
  );
}

function SearchIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M4 12l5 5L20 6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}